import {select} from "../../../shared/SagaService";
import {Type} from "../../Transaction/helper";
import {getDefaultFormDesign} from "../../../components/Select/FormDesign";
import dayjs from 'dayjs';
import {getInitData} from "../../Transaction/initTransaction/helper/getInitData";
import {parseTransactionForInstruction} from "./helpers/parseTransactionForInstruction";
import {readTransaction} from "../../Transaction/initTransaction/helper/readTransaction";
import {getLists} from "../../Transaction/initTransaction/helper/getLists";
import {readLists} from "../../Transaction/initTransaction/helper/readLists";
import {NumberFormatHelper} from "../../../components/NumberFormat/helpers";
import {deleteAllIds} from "../../Transaction/initTransaction/helper/deleteAllIds";
import {getDefaultReplyToEmail} from "../../../helpers/getDefaultReplyToEmail";
import {getServerUrl} from "../../Transaction/helpers/getServerUrl";
import { SubscriptionCheck } from "../../../components/Access/helper/subscription/SubscriptionCheck";
import { SubscriptionScopeKey } from "../../../components/Access/helper/subscription/enum/SubscriptionScopeKey";
import { processFormItems } from "../../Transaction/initTransaction/helper/processFormItems";
import { showConfirmationModalPromise } from "../../Transaction/initTransaction/helper/Modal/showConfirmationModalPromise";
import InstructionActions from "../actions";
import { put, take } from "redux-saga/effects";

const {
    READ_NEXT_TRANSACTION_DATE_REQUEST,
} = InstructionActions;

export function* create(matchParams, context) {
    const { id, type } = matchParams;

    const settings = yield select(state => state.List.settings);
    const formDesigns = yield select(state => state.List.formDesigns);
    const user = yield select(state => state.Auth.user);
    const productList = yield select(state => state.List.productList);
    const companyCurrencyCode = yield select(state => state.Auth.companyCurrencyCode);

    // Step 1: Init Instruction
    const transactionType = type || Type.sale_invoice;
    const defaultFormDesign = getDefaultFormDesign(formDesigns, settings, transactionType);

    const emailPanel = {
        cc_email_addresses: settings.e_invoice_cc_email_addresses,
        reply_to_email: getDefaultReplyToEmail(settings, user),
        email_subject: settings.default_email_subject,
        email_message: settings.email_message_sale_invoice,
        email_form_design_id: defaultFormDesign?.id,
        email_attach_pdf: settings.e_invoice_attachment_on,
    };
    const instruction = {
        date_from: dayjs().format("YYYY-MM-DD"),
        transaction_type: transactionType,
        transaction_status: 'ready',
        frequency: 'month',
        every: 1,
        day: 1,
        send_email: transactionType === Type.sale_invoice,
        post_dating: null,
        batch_processing: null,

        // panelEmail
        ...transactionType === Type.sale_invoice ? emailPanel : {},
    };

    // Get from api the transaction next date based on instruction init values
    yield put({
        type: READ_NEXT_TRANSACTION_DATE_REQUEST,
        request: {
            date_from: instruction.date_from,
            date_to: instruction.date_to,
            frequency: instruction.frequency,
            every: instruction.every,
            day: instruction.day,
            post_dating: instruction.post_dating,
            batch_processing: instruction.batch_processing,
        },
    });

    // Wait until it is read successfully before proceeding
    yield take(InstructionActions.READ_NEXT_TRANSACTION_DATE_SUCCESS);
    // Set the next transaction date in the instruction. This will be saved into nextTransactionDate in redux on INIT_INSTRUCTION_SUCCESS
    const nextTransactionDate = yield select(state => state.Instruction.nextTransactionDate);
    instruction.date_next = nextTransactionDate;

    // Step 2: Read Lists for Transaction
    const lists = getLists(transactionType);
    yield readLists(lists);

    // Step 2: Init Transaction

    let transaction;
    if (id) {
        const hasTagSub = SubscriptionCheck.withKeys(SubscriptionScopeKey.TAG).hasSubscriptionTo();
        const hasMultiCurrencySub =
            SubscriptionCheck.withKeys(SubscriptionScopeKey.MULTI_CURRENCY).hasSubscriptionTo();
        const hasCustomFieldSub =
            SubscriptionCheck.withKeys(SubscriptionScopeKey.CUSTOM_FIELD).hasSubscriptionTo();

        transaction = yield readTransaction(`${getServerUrl(transactionType)}/${id}`,
            transactionType,
            context);
        transaction.number = NumberFormatHelper.getDefaultNumberFormat(transactionType);
        transaction = deleteAllIds(transaction);

        let hasTagChange, hasCurrencyCodeChange, hasFieldChange;

        // remove data based on subscription
        if (!hasTagSub && (transaction.tag_ids || []).length !== 0) {
            transaction.tag_ids = [];
            hasTagChange = true;
        }

        if (!hasMultiCurrencySub && transaction.currency_code !== companyCurrencyCode) {
            transaction.currency_code = companyCurrencyCode;
            transaction.exchange_rate = 1;
            hasCurrencyCodeChange = true;
        }

        if (!hasCustomFieldSub && (transaction.fields || []).length !== 0) {
            transaction.fields = [];
            hasFieldChange = true;
        }

        let dataIndex;
        switch (transactionType) {
            case Type.bank_expense:
            case Type.bank_income:
                dataIndex = 'bank_items';
                break;
            default:
                dataIndex = 'form_items';
                break;
        }

        let nonBaseUomItems, removedBundles;
        let hasTaxChange, hasLocationChange;
        let finalFormItems = transaction[dataIndex];

        // process form items/bank items
        if (transaction[dataIndex]) {
            const {
                // processedFormItems,
                finalFormItems: finalFormItemsFunc,
                nonBaseUomItems: nonBaseUomItemsFunc,
                hasTaxChange: hasTaxChangeFunc,
                hasLocationChange: hasLocationChangeFunc,
                removedBundles: removedBundlesFunc,
            } = processFormItems({
                formItems: transaction[dataIndex] || [],
                productList,
                settings,
            });

            finalFormItems = finalFormItemsFunc;
            hasTaxChange = hasTaxChangeFunc;
            hasLocationChange = hasLocationChangeFunc;
            nonBaseUomItems = nonBaseUomItemsFunc;
            removedBundles = removedBundlesFunc;

            // console.log({processedFormItems, finalFormItems, nonBaseUomItems});
            // REMOVE non-base uom items
            // if (nonBaseUomItems?.length) {
            //     const invalidUoms = Array.from(new Set(nonBaseUomItems.map(i => i.product_unit_label)));
            //     // yield to make the Modal.confirm synchronous by using Promise
            //     yield showConfirmationModalPromise(invalidUoms)
            //         .then(() => {
            //             // User clicked OK - remove invalid form items with non-base UOM
            //             transaction[dataIndex] = finalFormItems;
            //         })
            //         .catch(() => {
            //             // // User clicked Cancel - retain all form items
            //             // transaction[dataIndex] = processedFormItems;
            //         });
            // }
            // else {
            //     transaction[dataIndex] = finalFormItems; // previously is processedFormItems
            // }
        }

        // show changes modal if there are changes made to created instruction due to change of subscription features
        if (nonBaseUomItems?.length || removedBundles?.length ||
            hasTagChange || hasCurrencyCodeChange || hasFieldChange || hasLocationChange || hasTaxChange
        ) {
            // yield to make the Modal.confirm synchronous by using Promise
            yield showConfirmationModalPromise(
                nonBaseUomItems,
                removedBundles,
                {
                    hasTagChange, hasCurrencyCodeChange, hasFieldChange, hasLocationChange, hasTaxChange,
                })
                .then(() => {
                    // User clicked OK - remove invalid form items with non-base UOM
                    transaction[dataIndex] = finalFormItems;
                });
        }
        else {
            transaction[dataIndex] = finalFormItems;
        }
    }
    else {
        transaction = yield getInitData(transactionType);
    }

    transaction = parseTransactionForInstruction(transactionType, transaction);
    transaction.exchange_rate = null;

    return [instruction, transaction, 'create'];
}
